import moment from 'moment';
import TempFieldUtils from '../TempFieldUtils';

const validator = require('validator');

export const createDisplayOrderNumber = (orderNumber: string): string => [...orderNumber]
  .filter((a) => /[0-9a-zA-Z]/.test(a)) // 获取字母和数字
  .reduce((a, b) => `${a}${b}`, '') // 拼接为字符串
  .slice(0, 6) // 截取6位
  .toUpperCase();

/** Strip Apollo Cache typename identifier so data can be used as inputs */
export const sanitizeData = <T = unknown>(data: T & { __typename?: string }): T => {
  if (data && typeof data === 'object') {
    const { __typename, ...safeData } = data;
    return safeData as unknown as T; // Learn more about TS and investigate
  }
  return data;
};

export const convertNumberWithComma = (value: string): string => parseInt(value, 10)
  .toLocaleString();

/** 转换日期，后面加st、nd、rd、th */
export const ordinal_suffix_of = (i: number) => {
  const j = i % 10,
    k = i % 100;
  if (j === 1 && k !== 11) {
    return i.toString().concat('st');
  }
  if (j === 2 && k !== 12) {
    return i.toString().concat('nd');
  }
  if (j === 3 && k !== 13) {
    return i.toString().concat('rd');
  }
  return i.toString().concat('th');
};

/** 月份英文简写 */
export const monthEnglish = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * 获取浏览器宽度
 * @returns {number}
 */
export function getWindowWidth() {
  let winWidth = 0;
  // 获取窗口宽度
  if (window.innerWidth) {
    winWidth = window.innerWidth;
  } else if ((document.body) && (document.body.clientWidth)) {
    winWidth = document.body.clientWidth;
  }
  // 通过深入Document内部对body进行检测，获取窗口大小
  else if (document.documentElement && document.documentElement.clientWidth) {
    winWidth = document.documentElement.clientWidth;
  }
  return winWidth;
}

/**
 * 获取菜品颜色
 * @param {string} colorName
 */
export function getMealColor(colorName: string | undefined) {
  if (colorName === 'pink') {
    return '#FBC0CB';
  }
  if (colorName === 'green') {
    return '#00B595';
  }
  if (colorName === 'lightBlue') {
    return '#5BC2E7';
  }
  if (colorName === 'orange') {
    return '#F28D41';
  }
  if (colorName === 'darkBlue') {
    return '#466277';
  }
  if (colorName === 'yellow') {
    return '#DDD76F';
  }
  if (colorName === 'black') {
    return '#000000';
  }
  if (colorName === 'white') {
    return '#FFFFFF';
  }
  return '';
}

/**
 * 获取浏览器高度
 * @returns {number}
 */
export function getWindowHeight() {
  let winHeight = 0;
  // 获取窗口高度
  if (window.innerHeight) {
    winHeight = window.innerHeight;
  } else if ((document.body) && (document.body.clientHeight)) {
    winHeight = document.body.clientHeight;
  }
  // 通过深入Document内部对body进行检测，获取窗口大小
  else if (document.documentElement && document.documentElement.clientHeight) {
    winHeight = document.documentElement.clientHeight;
  }
  return winHeight;
}

/**
 * 判断输入框中输入的日期格式为yyyy-mm-dd和正确的日期
 * @param date
 * @returns {boolean}
 * @constructor
 */
export function isDate(date: string) {
  const reg = /^(\d{4})-(\d{2})-(\d{2})$/;
  if (date === '') return true;
  return reg.test(date) && validator.isISO8601(date);
}

/**
 * 获取两个日期之间的日期，例如：2021-12-12~2021-12-14之间的日期：[2021-12-12, 2021-12-13, 2021-12-14]
 * @param {string} startDate
 * @param {string} endDate
 * @returns {any}
 */
export function getTwoDateDays(startDate: string, endDate: string) {
  let dates: string[] = [];
  try {
    if ((startDate && startDate.trim().length > 0 && isDate(startDate)) && (!endDate || endDate.trim().length === 0 || !isDate(endDate))) {
      dates.push(startDate);
      return dates;
    }

    if ((startDate && startDate.trim().length > 0 && isDate(startDate)) && (endDate && endDate.trim().length > 0 && isDate(endDate))) {
      const date1 = moment(startDate);
      const date2 = moment(endDate);
      const dayDiff = date2.diff(date1, 'day');
      dates = Array(dayDiff + 1).fill(null).map((day, i) => moment(date1).add(i, 'days').format('YYYY-MM-DD'));
    }
  } catch (e) {
    console.log(e);
  }
  return dates;
}

/**
 * 转换Url参数
 * @param {string} url
 */
export function parseUrlParams<T>(url: string) {
  const params: any = {};
  if (url) {
    let splits = decodeURI(url).split('?');
    if (splits.length > 1) {
      // 多个参数分割
      splits = splits[1].split('&');
      for (let i = 0; i < splits.length; i += 1) {
        const paramSplits = splits[i].split('=');
        if (paramSplits.length > 1) {
          params[paramSplits[0]] = paramSplits[1];
        }
      }
    }
  }
  return params as T;
}

/**
 * 获取平台名称：kitchen|admin
 */
export function getPlatform() {
  const pathName = window.document.location.pathname;
  const platform = pathName.substring(1, pathName.substr(1).indexOf('/') + 1);
  return platform;
}

/**
 * 计算总页数
 * @param pageSize
 * @param totalCount
 */
export function getPageCount(pageSize: number, totalCount: number) {
  return (totalCount % pageSize === 0 ? (parseInt((totalCount / pageSize).toString())) : (parseInt((totalCount / pageSize).toString()) + 1));
}

/**
 * 获取当前日期
 * yyyy-MM-dd
 */
export function getCurrentDate() {
  const dateTime = new Date();
  //月份
  let month: any = dateTime.getMonth() + 1;
  if (month < 10) {
    month = '0' + month;
  }
  //天数
  let day: any = dateTime.getDate();
  if (day < 10) {
    day = '0' + day;
  }

  return dateTime.getFullYear() + '-' + month + '-' + day;
}

/**
 * 日期格式化
 * @param dateTime Date日期对象
 * @param daySplit 日期分隔符，例如：“-” 或 “/”，默认“/”。结果展示：xx:xx xx/xx/xxxx，时:分 日/月/年
 */
export function getFormatDateTime(dateTime: Date | null, daySplit?: string) {
  if (!dateTime) {
    return '';
  }
  //月份
  let month: any = dateTime.getMonth() + 1;
  if (month < 10) {
    month = '0' + month;
  }
  //天数
  let day: any = dateTime.getDate();
  if (day < 10) {
    day = '0' + day;
  }
  //小时
  let hour: any = dateTime.getHours();
  if (hour < 10) {
    hour = '0' + hour;
  }
  //分钟
  let minute: any = dateTime.getMinutes();
  if (minute < 10) {
    minute = '0' + minute;
  }

  return hour + ':' + minute + ' ' + day + (daySplit || '/') + month + (daySplit || '/' + dateTime.getFullYear());
}

/**
 截取Float数据类型，动态保留小数点后几位
 @param floatVal float数据
 @param length
 */
export function getFloatSubStr(floatVal: any, length?: number) {
  // 数值保留小数点，如果后面全是0，则去掉后面的0。如果去掉0后，还剩下小数点，也需要把小数点去掉。
  let value: any = parseFloat(floatVal).toFixed(length == null ? 2 : length).replace(/[0]+$/, "");
  if (/\.$/.test(value)) {
    value = value.replace(/\.$/, "");
  }
  else {
    // 只显示小数点后两位非0数字。
    value = new RegExp("(-?\\d+\\.[0]*[1-9]{1," + (length == null ? 2 : length) + "})").exec(value);
  }
  if (value != null) {
    // 没有小数点的，不需要转换
    if (/^-?\d+$/.test(value)) {
      return value;
    }
    else {
      value = value[0];
    }
  }
  return value;
}

/**
 判断是否为空
 @param obj
 */
export function isEmpty(obj: any) {
  try {
    if (obj === null || obj === undefined) {
      return true;
    }
    //判断数字是否是NaN
    if (typeof obj === "number") {
      return isNaN(obj);
    }
    //判断参数是否是布尔、函数、日期、正则，是则返回false
    if (typeof obj === "boolean" || typeof obj === "function" || obj instanceof Date || obj instanceof RegExp) {
      return false;
    }
    //判断参数是否是字符串，去空，如果长度为0则返回true
    if (typeof obj === "string") {
      return obj.trim().length === 0;
    }

    if (typeof obj === 'object') {
      //判断参数是否是数组，数组为空则返回true
      if (obj instanceof Array) {
        return obj.length === 0;
      }

      //判断参数是否是对象，判断是否是空对象，是则返回true
      if (obj instanceof Object) {
        //判断对象属性个数
        return Object.getOwnPropertyNames(obj).length === 0;
      }
    }
  } catch (e) {
    console.log(e);
    return false;
  }
}

/**
 判断是否不为空
 @param obj
 */
export function isNotEmpty(obj: any) {
  try {
    if (obj === null || obj === undefined) {
      return false;
    }
    //判断数字是否是NaN
    if (typeof obj === "number") {
      return !isNaN(obj);
    }
    //判断参数是否是布尔、函数、日期、正则，是则返回true
    if (typeof obj === "boolean" || typeof obj === "function" || obj instanceof Date || obj instanceof RegExp) {
      return true;
    }
    //判断参数是否是字符串，去空，如果长度为0则返回false
    if (typeof obj === "string") {
      return obj.trim().length !== 0;
    }

    if (typeof obj === 'object') {
      //判断参数是否是数组，数组为空则返回false
      if (obj instanceof Array) {
        return obj.length !== 0;
      }

      //判断参数是否是对象，判断是否是空对象，是则返回false
      if (obj instanceof Object) {
        //判断对象属性个数
        return Object.getOwnPropertyNames(obj).length !== 0;
      }
    }
  } catch (e) {
    console.log(e);
    return false;
  }
}

/**
 检查localStorage是否可用
 */
export function checkLocalStorageIsEnable() {
  try {
    localStorage.setItem('testLocalStorage', 'abc');
    if (localStorage.getItem('testLocalStorage') != null && localStorage.getItem('testLocalStorage') === "abc") {
      localStorage.removeItem('testLocalStorage');
      return true;
    }
  } catch (e) {
    console.log(e);
    return false;
  }
}

/**
 获取是不是英文
 */
export function getIsEnglish(cookies: any, setCookie: any) {
  try {
    // 本地存储的中英文码
    let localFoxLang = null;
    // 先从Cookies中获取数据
    const cookieValue = cookies.fox_h5_language;
    if (cookieValue) {
      localFoxLang = cookieValue;
    }
    // Cookies中没有数据时，从localStorage中获取数据
    if (localFoxLang == null && checkLocalStorageIsEnable()) {
      const value = localStorage.getItem("fox_h5_language");
      if (value) {
        localFoxLang = value;
      }
    }

    // 唯一字符串
    if (localFoxLang == null) {
      localFoxLang = navigator.language === "zh-CN" ? "CN" : "EN";
    }

    // 覆盖本地设备码
    // Cookies
    setCookie("fox_h5_language", localFoxLang, { expires: new Date((new Date().getTime()) + 1000 * 60 * 60 * 24 * 365), path: "/" });
    // localStorage
    localStorage.setItem('fox_h5_language', localFoxLang);

    return localFoxLang === 'EN';
  } catch (e) {
    console.log(e);
    return true;
  }
}

/**
 判断是不是Android
 */
export function isAndroid() {
  try {
    const u = navigator.userAgent;
    return u.includes('Android') || u.includes('Linux');
  } catch (e) {
    console.log(e);
    return false;
  }
}

/**
 * 从字符串中获取数字
 * @param str
 */
export function getNumberInString(str: string) {
  return str.split(/\D+/).filter(Boolean).join('');
}

/**
 * 把柜子ID转成36进制
 * @param cabId
 */
export function getCabId32Str(cabId: number) {
  return (cabId + 9527).toString(36);
}

/**
 * 把36进制柜子ID转成10进制
 * @param cabId32Str
 */
export function getCabId(cabId32Str: string) {
  return parseInt(cabId32Str, 36) - 9527;
}

/**
 * 睡眠
 * @param time
 */
export function sleep(time: number) {
  return new Promise((resolve) => setTimeout(resolve, time));
}

/**
 * 获取中英文
 * @param en
 * @param zh
 */
export const lang = (en: any, zh: any) => {
  return (TempFieldUtils.currentIsEnglish == null || TempFieldUtils.currentIsEnglish) ? en : zh;
};

/**
 * 获取中英文
 * @param phoneNumber
 * @param showSpace
 * @param showCountryCode
 */
export const getFormatPhoneNumber = (phoneNumber: string, showSpace?: boolean, showCountryCode?: boolean) => {
  const curPhoneNumber = phoneNumber.startsWith('61') ? `0${phoneNumber.substring(2)}` : `0${phoneNumber}`;
  if (showSpace) {
    const curPhoneNumber = phoneNumber.startsWith('61') ? `0${phoneNumber.substring(2)}` : `0${phoneNumber}`;
    return (showCountryCode ? '+61 ' : '') + (curPhoneNumber.substring(showCountryCode ? 1 : 0, 4)) + ' ' + (curPhoneNumber.substring(4, 7)) + ' ' + (curPhoneNumber.substring(7, 10));
  }
  return (showCountryCode ? '+61' : '') + (curPhoneNumber.substring(showCountryCode ? 1 : 0, 10));
};

/**
 * https://stackoverflow.com/a/46181
 * Validates that an email is well-formed & legitimate
 * @param email The email address that will be validated
 */
export const isEmailValid = (email?: string) => {
  if (email == null) {
    return false;
  }
  const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return emailRegex.test(email);
};

/**
 * 获取随机整数
 * @param min
 * @param max
 */
export const getRandomIntInRange = (min: number, max: number) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * 判断是否是手机浏览器
 * @returns
 */
export const isMobile = () => {
  const userAgent = navigator.userAgent;
  return /Android|webOS|iPhone|iPad|iPod/i.test(userAgent);
};

export default {
  createDisplayOrderNumber,
  sanitizeData,
  convertNumberWithComma,
  isDate,
  ordinal_suffix_of,
  getTwoDateDays,
  parseUrlParams,
  getPlatform,
  getFloatSubStr,
};
